import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Funnel, X, ArrowsClockwise, CaretUp, CaretDown,
  Check, Trash, ListChecks, MagnifyingGlass, Sparkle, Plus, CheckCircle, Prohibit,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import ElementDetailDrawer from './ElementDetailDrawer';
import TaskValidateModal from './TaskValidateModal';
import ActionCreateDialog from './ActionCreateDialog';
import PrevenirEntreprises from './PrevenirEntreprises';
import { useAuth } from '../../contexts/AuthContext';
import { BOUTONS, OBJETS, STATUT_ACTION, TYPE_ELEMENT, libelleStatut } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CHA-09 — Changement d'état pour les deux sources fusionnées par GET /elements.
// Avant : un item legacy (source_table='taches') partait en PUT /taches/{id}
// {statut:'archivee'} → 422 sur « Ignorer » (statut hors TacheUpdate).
// PUT /elements/{id}/etat traduit désormais lui-même l'état pour une tâche
// legacy (backend/services/taches_legacy_etat.py) : un seul appel pour tous.
async function dispatchEtatChange(chantierId, id, newEtat, sourceTable) {
  return axios.put(
    `${API_URL}/api/chantiers/${chantierId}/elements/${id}/etat`,
    { etat: newEtat },
    { withCredentials: true },
  );
}

const TYPE_CONFIG = {
  reserve:             { label: 'Réserve',         color: '#C75B39' },
  tache:               { label: TYPE_ELEMENT.tache, color: '#1E3A5F' },
  incident:            { label: 'Incident',        color: '#C00000' },
  non_conformite:      { label: 'Non-conformité',  color: '#8B3A3A' },
  demande_client:      { label: 'Demande client',  color: '#7B5EA7' },
  question_entreprise: { label: 'Question',        color: '#5B8FB9' },
  observation:         { label: 'Observation',     color: '#6B6B6B' },
  validation:          { label: 'Validation',      color: '#2D6A4F' },
  decision:            { label: 'Décision',        color: '#1B4965' },
  info:                { label: 'Info',            color: '#9A9A9A' },
};

const PRIORITE_BADGE = {
  urgent: { label: 'Urgent', bg: '#C00000', fg: 'white' },
  normal: { label: 'Normal', bg: '#E0E0DB', fg: '#2C2C2C' },
  basse:  { label: 'Basse',  bg: '#F5F5F3', fg: '#9A9A9A' },
};

// RP14 (ARC-R5) — liste unique « Actions » du chantier (remplace les sous-onglets
// « Actions des CR » / « Actions du chantier » du Suivi). RP15-16 — « À vérifier » :
// levées proposées par l'entreprise (preuve apportée), à valider ou refuser.
// `a_valider` = proposition de l'Assistant pas encore retenue (« Propositions ») : le
// libellé « À vérifier » est réservé aux levées (09-VOCABULAIRE §3).
const ETAT_OPTIONS = [
  { key: 'a_valider',  label: 'Propositions' },
  { key: 'actif',      label: STATUT_ACTION.actif },
  { key: 'a_verifier', label: STATUT_ACTION.a_verifier },
  { key: 'fait',       label: STATUT_ACTION.fait },
  { key: 'ignore',     label: STATUT_ACTION.ignore },
];
const ETATS_CONNUS = ETAT_OPTIONS.map((e) => e.key);

const ECHEANCE_OPTIONS = [
  { key: '', label: 'Toutes échéances' },
  { key: 'retard', label: 'En retard' },
  { key: 'semaine', label: 'Dans les 7 jours' },
  { key: 'sans', label: 'Sans échéance' },
];

function dansTrancheEcheance(echeance, tranche) {
  if (!tranche) return true;
  if (tranche === 'sans') return !echeance;
  if (!echeance) return false;
  const e = String(echeance).slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  if (tranche === 'retard') return e < today;
  const fin = new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10);
  return e >= today && e <= fin;
}

const SOURCE_OPTIONS = [
  { key: '', label: 'Toutes sources' },
  { key: 'fil', label: 'Fil terrain' },
  { key: 'visite', label: 'Visite' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'creation_manuelle', label: 'Création manuelle' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function ProjectTasks({ chantierId }) {
  const { user: currentUser } = useAuth();
  // Lien profond (notification « Levée à vérifier ») : ?tab=taches&etat=a_verifier
  const [searchParams] = useSearchParams();
  const etatInitial = ETATS_CONNUS.includes(searchParams.get('etat')) ? searchParams.get('etat') : 'actif';
  const [elements, setElements] = useState([]);
  const [marches, setMarches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [leveeEnCours, setLeveeEnCours] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    etat: etatInitial,
    type: '',
    entreprise: '',
    lot: '',
    priorite: '',
    source: '',
    search: '',
    crc_only: false,
    crc: '',
    echeance: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  // PATCH v6 Sprint 2 — Toggle "Mes tâches" / "Toutes les tâches"
  const [filterMode, setFilterMode] = useState('all');  // 'all' | 'mine'

  // Tri
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Drawer
  const [activeElement, setActiveElement] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // D.FIL.5 — Modal de validation pour les propositions IA (a_valider)
  const [validateElement, setValidateElement] = useState(null);
  const [validateOpen, setValidateOpen] = useState(false);

  // D.FIL.5 — Ignorer rapide d'une proposition a_valider
  const ignoreElement = async (eid) => {
    if (!eid) return;
    if (!window.confirm('Classer sans suite cette proposition de l\'Assistant ?')) return;
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/elements/${eid}/etat`,
        { etat: 'ignore' },
        { withCredentials: true },
      );
      toast.success('Proposition ignorée');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  // RP16 — l'architecte valide ou refuse la levée proposée par l'entreprise.
  const validerLevee = async (e) => {
    const eid = e._id || e.id;
    setLeveeEnCours(eid);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/elements/${eid}/valider-levee`, {}, { withCredentials: true });
      toast.success('Levée validée : elle figurera au prochain compte rendu');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setLeveeEnCours(null);
    }
  };

  const refuserLevee = async (e) => {
    const motif = window.prompt('Motif du refus (transmis à l\'entreprise) :');
    if (motif === null) return;
    if (!motif.trim()) {
      toast.warning('Indiquez le motif du refus');
      return;
    }
    const eid = e._id || e.id;
    setLeveeEnCours(eid);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/elements/${eid}/refuser-levee`,
        { motif: motif.trim() },
        { withCredentials: true },
      );
      toast.info('Levée refusée : l\'action repasse à faire');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setLeveeEnCours(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ etat: filters.etat, limit: '500' });
      if (filters.type) params.set('type', filters.type);
      if (filters.entreprise) params.set('entreprise', filters.entreprise);
      if (filters.source) params.set('source', filters.source);
      // PATCH v6 — Filtre "Mes tâches" : on passe assigned_to=user._id au backend
      if (filterMode === 'mine' && currentUser?._id) {
        params.set('assigned_to', String(currentUser._id));
      }

      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/elements?${params.toString()}`,
        { withCredentials: true },
      );
      const items = Array.isArray(data) ? data : data?.items || [];
      setElements(items);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Erreur chargement éléments');
      setElements([]);
    } finally {
      setLoading(false);
    }
  }, [chantierId, filters.etat, filters.type, filters.entreprise, filters.source, filterMode, currentUser]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then((r) => setMarches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMarches([]));
  }, [chantierId]);

  const lotsUnique = useMemo(() => [...new Set(marches.map((m) => m.nom).filter(Boolean))], [marches]);
  const entreprisesUnique = useMemo(() => [...new Set(marches.map((m) => m.entreprise_nom).filter(Boolean))], [marches]);
  // RP14 — filtre « par compte rendu » (ex-« Actions des CR ») : CR d'origine des actions chargées
  const crcsUnique = useMemo(() => {
    const map = new Map();
    for (const e of elements) {
      for (const c of (e.crcs_origine_enriched || [])) {
        if (c && c.id && !map.has(c.id)) map.set(c.id, c);
      }
    }
    return [...map.values()].sort((a, b) => (b.numero || 0) - (a.numero || 0));
  }, [elements]);

  // Filtrage client (lot/priorite/search/crc_only)
  const filtered = useMemo(() => {
    const search = (filters.search || '').toLowerCase().trim();
    return elements.filter((e) => {
      if (filters.lot && e.lot !== filters.lot) return false;
      if (filters.priorite && e.priorite !== filters.priorite) return false;
      if (filters.crc_only && e.inclure_dans_prochain_crc === false) return false;
      if (filters.crc && !(e.crcs_origine || []).includes(filters.crc)) return false;
      if (!dansTrancheEcheance(e.echeance, filters.echeance)) return false;
      if (search) {
        const blob = `${e.titre || ''} ${e.description || ''} ${e.texte_brut || ''} ${e.entreprise || ''} ${e.lot || ''}`.toLowerCase();
        if (!blob.includes(search)) return false;
      }
      return true;
    });
  }, [elements, filters.lot, filters.priorite, filters.search, filters.crc_only, filters.crc, filters.echeance]);

  // Tri
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const valA = (a[sortBy] || '').toString();
      const valB = (b[sortBy] || '').toString();
      if (sortDir === 'asc') return valA.localeCompare(valB);
      return valB.localeCompare(valA);
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sorted.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map((e) => e._id || e.id)));
    }
  };

  // Bulk actions — Iter 88: dispatcher selon source_table (elements vs taches legacy)
  const bulkChangeEtat = async (newEtat) => {
    if (selectedIds.size === 0) return;
    if (newEtat === 'ignore' && !window.confirm(`Classer sans suite ${selectedIds.size} action(s) ?`)) return;
    let ok = 0, ko = 0;
    for (const id of selectedIds) {
      const item = sorted.find((e) => (e._id || e.id) === id);
      const sourceTable = item?.source_table || 'elements';
      try {
        await dispatchEtatChange(chantierId, id, newEtat, sourceTable);
        ok += 1;
      } catch (err) {
        console.error('[bulkChangeEtat] dispatch failed', { id, sourceTable, newEtat, err });
        ko += 1;
      }
    }
    if (ok) toast.success(`${ok} élément${ok > 1 ? 's' : ''} mis à jour`);
    if (ko) toast.error(`${ko} échec${ko > 1 ? 's' : ''}`);
    load();
  };

  const exportCsv = () => {
    const targets = selectedIds.size > 0 ? sorted.filter((e) => selectedIds.has(e._id || e.id)) : sorted;
    const cols = ['date', 'type', 'titre', 'entreprise', 'lot', 'priorite', 'echeance', 'duree_jours', 'lieu', 'etat', 'inclure_crc'];
    const rows = [cols.join(';')];
    for (const e of targets) {
      rows.push([
        fmtDate(e.created_at),
        e.type || '',
        (e.titre || e.texte_brut || '').replace(/[\n;]/g, ' ').slice(0, 200),
        e.entreprise || '',
        e.lot || '',
        e.priorite || '',
        e.echeance || '',
        e.duree_jours || 1,
        (e.lieu || '').replace(/[\n;]/g, ' '),
        e.etat ? libelleStatut(STATUT_ACTION, e.etat) : '',
        e.inclure_dans_prochain_crc !== false ? 'oui' : 'non',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'));
    }
    const blob = new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `elements_${chantierId}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${targets.length} élément${targets.length > 1 ? 's' : ''} exporté${targets.length > 1 ? 's' : ''}`);
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const resetFilters = () => setFilters({ etat: 'actif', type: '', entreprise: '', lot: '', priorite: '', source: '', search: '', crc_only: false, crc: '', echeance: '' });

  const sortIcon = (key) => sortBy === key ? (sortDir === 'asc' ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />) : null;

  const avecColonneTraiter = filters.etat === 'a_valider' || filters.etat === 'a_verifier';
  const allChecked = selectedIds.size > 0 && selectedIds.size === sorted.length;
  const someChecked = selectedIds.size > 0 && !allChecked;

  return (
    <div className="space-y-3" data-testid="project-tasks-page">
      {/* B3 — « 8 actions validées : prévenir 3 entreprises ? » */}
      <PrevenirEntreprises chantierId={chantierId} rafraichir={elements} onPrevenu={() => load()} />
      {/* Filtres principaux */}
      <div className="bg-white border border-[#E0E0DB] p-3 space-y-3" data-testid="project-tasks-filters">
        {/* PATCH v6 Sprint 2 — Toggle Mes tâches / Toutes */}
        <div className="flex items-center gap-2 pb-2 border-b border-[#F0F0EC]">
          <span className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Vue :</span>
          <div className="flex items-center bg-[#F8F7F4] border border-[#E0E0DB]">
            <button
              onClick={() => setFilterMode('mine')}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                filterMode === 'mine' ? 'bg-[#1E3A5F] text-white' : 'text-[#6B6B6B] hover:text-[#2C2C2C]'
              }`}
              data-testid="project-tasks-filter-mine"
            >
              Mes actions
            </button>
            <button
              onClick={() => setFilterMode('all')}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                filterMode === 'all' ? 'bg-[#1E3A5F] text-white' : 'text-[#6B6B6B] hover:text-[#2C2C2C]'
              }`}
              data-testid="project-tasks-filter-all"
            >
              Toutes les actions
            </button>
          </div>
          {filterMode === 'mine' && (
            <span className="text-xs text-[#6B6B6B] italic">→ assignées à moi sur ce chantier</span>
          )}
          {/* RP14 (ARC-R5) — « + Action » : créer une action depuis la liste unique */}
          <Button
            className="ml-auto rounded-none bg-[#C75B39] hover:bg-[#D97B5C] h-8 px-3 text-xs"
            onClick={() => setCreateOpen(true)}
            data-testid="project-tasks-add-action"
          >
            <Plus size={14} weight="bold" className="mr-1" /> {OBJETS.action}
          </Button>
        </div>

        {/* Ligne 1 : etat + recherche + advanced toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-[#F8F7F4] border border-[#E0E0DB]">
            {ETAT_OPTIONS.map((e) => (
              <button
                key={e.key}
                onClick={() => setFilter('etat', e.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  filters.etat === e.key ? 'bg-[#1E3A5F] text-white' : 'text-[#6B6B6B] hover:text-[#2C2C2C]'
                }`}
                data-testid={`project-tasks-etat-${e.key}`}
              >
                {e.label}
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
            <input
              type="text"
              placeholder="Rechercher titre, description, entreprise..."
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-[#E0E0DB] text-sm rounded-none"
              data-testid="project-tasks-search"
            />
          </div>

          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setShowAdvanced((s) => !s)}
            data-testid="project-tasks-advanced-toggle"
          >
            <Funnel size={14} className="mr-1" /> {showAdvanced ? 'Masquer' : 'Filtres'}
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={load}
            data-testid="project-tasks-reload"
          >
            <ArrowsClockwise size={14} />
          </Button>
        </div>

        {/* Ligne 2 : filtres avancés */}
        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-[#E0E0DB]" data-testid="project-tasks-advanced-filters">
            <select
              value={filters.type}
              onChange={(e) => setFilter('type', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-type"
            >
              <option value="">Tous types</option>
              {Object.entries(TYPE_CONFIG).map(([k, c]) => (
                <option key={k} value={k}>{c.label}</option>
              ))}
            </select>
            <select
              value={filters.entreprise}
              onChange={(e) => setFilter('entreprise', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-entreprise"
            >
              <option value="">Toutes entreprises</option>
              {entreprisesUnique.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <select
              value={filters.lot}
              onChange={(e) => setFilter('lot', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-lot"
            >
              <option value="">Tous lots</option>
              {lotsUnique.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select
              value={filters.priorite}
              onChange={(e) => setFilter('priorite', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-priorite"
            >
              <option value="">Toutes priorités</option>
              <option value="urgent">Urgent</option>
              <option value="normal">Normal</option>
              <option value="basse">Basse</option>
            </select>
            <select
              value={filters.crc}
              onChange={(e) => setFilter('crc', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-compte-rendu"
            >
              <option value="">Tous les comptes rendus</option>
              {crcsUnique.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.numero ? `CR n° ${c.numero}` : 'Compte rendu'}{c.date ? ` du ${new Date(c.date).toLocaleDateString('fr-FR')}` : ''}
                </option>
              ))}
            </select>
            <select
              value={filters.echeance}
              onChange={(e) => setFilter('echeance', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-echeance"
            >
              {ECHEANCE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <select
              value={filters.source}
              onChange={(e) => setFilter('source', e.target.value)}
              className="px-2 py-1.5 border border-[#E0E0DB] text-xs rounded-none bg-white"
              data-testid="project-tasks-filter-source"
            >
              {SOURCE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs col-span-1" data-testid="project-tasks-filter-crc-label">
              <input
                type="checkbox"
                checked={filters.crc_only}
                onChange={(e) => setFilter('crc_only', e.target.checked)}
                data-testid="project-tasks-filter-crc"
              />
              Comptes rendus seulement
            </label>
            <Button
              variant="outline"
              className="rounded-none text-xs col-span-2 md:col-span-2"
              onClick={resetFilters}
              data-testid="project-tasks-reset"
            >
              <X size={12} className="mr-1" /> Réinitialiser
            </Button>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div
          className="bg-[#1E3A5F] text-white px-4 py-2.5 flex flex-wrap items-center gap-3"
          data-testid="project-tasks-bulk-bar"
        >
          <span className="text-sm font-semibold">{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
          <Button
            size="sm"
            variant="outline"
            className="bg-white text-[#1E3A5F] hover:bg-[#FAFAF8] rounded-none border-white"
            onClick={() => bulkChangeEtat('fait')}
            data-testid="project-tasks-bulk-fait"
          >
            <Check size={12} className="mr-1" /> Marquer levée
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-white text-[#1E3A5F] hover:bg-[#FAFAF8] rounded-none border-white"
            onClick={() => bulkChangeEtat('ignore')}
            data-testid="project-tasks-bulk-ignorer"
          >
            <Trash size={12} className="mr-1" /> Sans suite
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-white text-[#1E3A5F] hover:bg-[#FAFAF8] rounded-none border-white"
            onClick={exportCsv}
            data-testid="project-tasks-bulk-export"
          >
            <ListChecks size={12} className="mr-1" /> Export CSV
          </Button>
          <button
            className="ml-auto text-white/80 hover:text-white text-xs"
            onClick={() => setSelectedIds(new Set())}
            data-testid="project-tasks-bulk-clear"
          >
            ✕ Désélectionner
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E0E0DB] overflow-x-auto" data-testid="project-tasks-table-wrap">
        <table className="w-full text-sm" data-testid="project-tasks-table">
          <thead className="bg-[#F8F7F4] border-b border-[#E0E0DB]">
            <tr>
              <th className="w-10 px-3 py-2.5 text-left">
                <Checkbox
                  checked={allChecked}
                  onCheckedChange={toggleSelectAll}
                  className={someChecked ? 'data-[state=checked]:bg-[#9A9A9A]' : ''}
                  data-testid="project-tasks-checkbox-all"
                />
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                <div className="flex items-center gap-1">Date {sortIcon('created_at')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none" onClick={() => toggleSort('type')}>
                <div className="flex items-center gap-1">Type {sortIcon('type')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none" onClick={() => toggleSort('titre')}>
                <div className="flex items-center gap-1">Titre {sortIcon('titre')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none hidden md:table-cell" onClick={() => toggleSort('entreprise')}>
                <div className="flex items-center gap-1">Entreprise {sortIcon('entreprise')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none hidden md:table-cell" onClick={() => toggleSort('lot')}>
                <div className="flex items-center gap-1">Lot {sortIcon('lot')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none" onClick={() => toggleSort('priorite')}>
                <div className="flex items-center gap-1">Prio. {sortIcon('priorite')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] cursor-pointer select-none hidden lg:table-cell" onClick={() => toggleSort('echeance')}>
                <div className="flex items-center gap-1">Échéance {sortIcon('echeance')}</div>
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] hidden lg:table-cell">CR</th>
              {avecColonneTraiter && (
                <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B]" data-testid="project-tasks-col-actions">
                  Traiter
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={avecColonneTraiter ? 10 : 9} className="text-center py-10 text-[#9A9A9A]">Chargement…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={avecColonneTraiter ? 10 : 9} className="text-center py-12 text-[#9A9A9A]" data-testid="project-tasks-empty">
                Aucun élément ne correspond aux filtres.
              </td></tr>
            )}
            {!loading && sorted.map((e) => {
              const eid = e._id || e.id;
              const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.info;
              const isSelected = selectedIds.has(eid);
              const prio = PRIORITE_BADGE[e.priorite] || PRIORITE_BADGE.normal;
              return (
                <tr
                  key={eid}
                  className={`border-b border-[#E0E0DB] cursor-pointer transition-colors ${isSelected ? 'bg-[#FFF8E7]' : 'hover:bg-[#FAFAF8]'}`}
                  onClick={() => {
                    // Iter 88 — Le drawer ElementDetailDrawer pilote PUT /elements/{id}/{etat,…} et
                    // POST /comments. Sur une tache legacy (source_table='taches'), ces endpoints
                    // retournent 404. Pour ne pas casser l'UX en attente migration finale (Sprint 6),
                    // on bloque l'ouverture avec un message informatif. Bulk fait/ignore reste dispo.
                    if (e.source_table === 'taches') {
                      toast.info(
                        'Ancienne action : clôturez-la avec les boutons « Marquer levée » / « Sans suite » de la barre de sélection.',
                      );
                      return;
                    }
                    setActiveElement(e);
                    setDrawerOpen(true);
                  }}
                  data-testid={`project-tasks-row-${eid}`}
                >
                  <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(eid)}
                      data-testid={`project-tasks-row-check-${eid}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6B6B6B] whitespace-nowrap" title={fmtDateTime(e.created_at)}>
                    {fmtDate(e.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: cfg.color + '15', color: cfg.color }}
                    >
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#2C2C2C] max-w-md">
                    <div className="font-semibold truncate">{e.titre || e.texte_brut || '(sans titre)'}</div>
                    {e.lieu && <div className="text-xs text-[#9A9A9A] truncate">📍 {e.lieu}</div>}
                    {e.etat === 'a_verifier' && (e.preuves_entreprise || []).length > 0 && (() => {
                      const p = e.preuves_entreprise[e.preuves_entreprise.length - 1];
                      return (
                        <div className="text-xs text-[#7A5A12] truncate" data-testid={`project-tasks-preuve-${eid}`}>
                          Preuve{p.by_user_label ? ` de ${p.by_user_label}` : ''} : {p.photo_url ? '📷 photo' : ''}{p.photo_url && p.commentaire ? ' · ' : ''}{p.commentaire || ''}
                        </div>
                      );
                    })()}
                    {e.etat === 'actif' && e.levee_refusee_motif && (
                      <div className="text-xs text-[#8B2C2C] truncate">Levée refusée : {e.levee_refusee_motif}</div>
                    )}
                    {/* B3 — « À qui la balle » (calculé par le serveur) */}
                    {e.balle && (
                      <div
                        className={`text-xs truncate ${e.balle.cote === 'moe' ? 'text-[#9A5B13] font-semibold' : 'text-[#6B6B6B]'}`}
                        data-testid={`project-tasks-balle-${eid}`}
                      >
                        {e.balle.libelle}
                      </div>
                    )}
                    {e.etat === 'actif' && e.distribution?.sans_adresse && e.entreprise && (
                      <div className="text-xs text-[#8B3A3A] truncate">{e.entreprise} n'a pas d'adresse e-mail</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6B6B6B] hidden md:table-cell">{e.entreprise || '—'}</td>
                  <td className="px-3 py-2 text-xs text-[#6B6B6B] hidden md:table-cell">{e.lot_nom || e.lot || '—'}</td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase"
                      style={{ background: prio.bg, color: prio.fg }}
                    >
                      {prio.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6B6B6B] hidden lg:table-cell">
                    {e.echeance ? new Date(e.echeance).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs hidden lg:table-cell">
                    {e.inclure_dans_prochain_crc !== false ? <span className="text-[#2D6A4F]">✓</span> : <span className="text-[#9A9A9A]">—</span>}
                  </td>
                  {filters.etat === 'a_valider' && (
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none h-7 px-2 text-xs"
                          onClick={() => { setValidateElement(e); setValidateOpen(true); }}
                          data-testid={`project-tasks-validate-${eid}`}
                          title="Valider et attribuer"
                        >
                          <Sparkle size={11} weight="fill" className="mr-1" /> Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none h-7 px-2 text-xs text-[#9A9A9A] border-[#E0E0DB] hover:bg-[#FEF2F2] hover:text-[#DC2626] hover:border-[#DC2626]"
                          onClick={() => ignoreElement(eid)}
                          data-testid={`project-tasks-ignore-${eid}`}
                          title="Classer sans suite"
                        >
                          <Trash size={11} />
                        </Button>
                      </div>
                    </td>
                  )}
                  {filters.etat === 'a_verifier' && (
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          className="bg-[#2D6A4F] hover:bg-[#245740] text-white rounded-none h-7 px-2 text-xs"
                          disabled={leveeEnCours === eid}
                          onClick={() => validerLevee(e)}
                          data-testid={`project-tasks-valider-levee-${eid}`}
                        >
                          <CheckCircle size={11} weight="fill" className="mr-1" /> {BOUTONS.validerLevee}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none h-7 px-2 text-xs text-[#8B2C2C] border-[#E0E0DB] hover:bg-[#FEF2F2]"
                          disabled={leveeEnCours === eid}
                          onClick={() => refuserLevee(e)}
                          data-testid={`project-tasks-refuser-levee-${eid}`}
                        >
                          <Prohibit size={11} className="mr-1" /> {BOUTONS.refuserLevee}
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer count */}
      <div className="text-xs text-[#9A9A9A] flex items-center justify-between" data-testid="project-tasks-footer">
        <span>{sorted.length} élément{sorted.length > 1 ? 's' : ''} affiché{sorted.length > 1 ? 's' : ''} (sur {elements.length} chargé{elements.length > 1 ? 's' : ''})</span>
        {selectedIds.size > 0 && <span>{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>}
      </div>

      {/* Drawer */}
      <ElementDetailDrawer
        element={activeElement}
        chantierId={chantierId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdate={load}
        onRemove={(id) => {
          setElements((prev) => prev.filter((x) => (x._id || x.id) !== id));
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }}
      />

      {/* RP14 — « + Action » */}
      <ActionCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        chantierId={chantierId}
        onCreated={() => { if (filters.etat !== 'actif') setFilter('etat', 'actif'); else load(); }}
      />

      {/* D.FIL.5 — Modal Valider + Attribuer (proposition IA → tâche actif) */}
      <TaskValidateModal
        open={validateOpen}
        onClose={() => { setValidateOpen(false); setValidateElement(null); }}
        chantierId={chantierId}
        element={validateElement}
        onValidated={() => load()}
      />
    </div>
  );
}
